import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import { hasAnyOverAllocatedVolume } from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'
import { cascadeMarketToForecastMarket, isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity, hasActivationGroupsConcept } from '@/features/bidding/utils/marketCapabilities'

type UseReviewBidsDisabledStateParams = {
  selectedMarket: CascadeMarketId
  levels: BidLevelInput[] | BidLevelInputSymmetrical[]
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null | undefined
  aggregateAvailability: AggregateAvailabilityResponse | null | undefined
  activationGroupsCount: number
}

type ReviewBidsDisabledState = {
  disabled: boolean
  tooltipReason: string
}

function useReviewBidsDisabledState({
  selectedMarket,
  levels,
  aggregateSuggestedBids,
  aggregateAvailability,
  activationGroupsCount,
}: Readonly<UseReviewBidsDisabledStateParams>): ReviewBidsDisabledState {
  const { t } = useTranslation()

  return useMemo(() => {
    // mFRR requires at least one activation group
    if (hasActivationGroupsConcept(selectedMarket)) {
      return { disabled: activationGroupsCount === 0, tooltipReason: '' }
    }

    // FCR markets: check for volume over-allocation
    const isSymmetrical = isSymmetricMarket(cascadeMarketToForecastMarket(selectedMarket))
    const { stepMW } = getMarketVolumeGranularity(selectedMarket)
    const overAllocated = hasAnyOverAllocatedVolume({
      levels,
      isSymmetrical,
      aggregateSuggestedBids,
      aggregateAvailability,
      granularityMW: stepMW,
    })

    if (overAllocated) {
      return {
        disabled: true,
        tooltipReason: t('bidding.bidding_cascading.review_bids.volume_over_allocated_tooltip'),
      }
    }

    return { disabled: false, tooltipReason: '' }
  }, [selectedMarket, activationGroupsCount, levels, aggregateSuggestedBids, aggregateAvailability, t])
}

export default useReviewBidsDisabledState
